<script setup lang="ts">
/*
 * Edit History drawer — an entity's audit trail (When · Who · Field · Old → New),
 * newest first, from the V2 audit service via `useEntityHistory`.
 *
 * The first page loads when the drawer opens; more pages load only when the
 * user presses "Load more" (no auto-paging — G-C). Values for `maskedFields`
 * (and values the server dropped for size) are never rendered. In an env where
 * the audit service is not on V2 the drawer shows an "unavailable" note and
 * sends no request.
 *
 * Readability: "Who" (and `…By` values) resolve to a person through ONE cached
 * identity user-list read (`useHistoryActors`) — unknown ids show short;
 * un-mapped field names are humanised; reference ids (`…Id`) show as a muted
 * "(id)"; no-op lines (empty → empty) are dropped. A 403 shows a permission
 * message (no retry); only network errors / 5xx are retried, once.
 *
 * Focus (F5): this drawer is opened from list pages AND from inside other
 * modals (Job Title / Grade / Profile). A Headless UI Dialog opened beside
 * another open Headless UI Dialog cannot take focus (the open one's focus trap
 * pulls it back) and loses Esc (the open one consumes it). So the drawer owns
 * its dialog behaviour instead: it teleports to `<body>` (Headless UI treats a
 * body-level sibling as an allowed container), moves focus in on open, traps
 * Tab, closes on Esc (a window capture listener that runs before Headless UI's
 * own), and returns focus to the opener on close.
 */
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { History, X } from 'lucide-vue-next';

import Button from './Button.vue';
import EmptyState from './EmptyState.vue';
import Skeleton from './Skeleton.vue';

import { isHistoryForbidden } from '@/api/services/audit';
import { useSelfActor } from '@/composables/useActorLabels';
import {
  collectActorIds,
  describeActor,
  describeHistoryValue,
  flattenHistory,
  humanizeFieldName,
  isKnownHistoryField,
  useEntityHistory,
  useHistoryActors,
  type HistoryRow,
  type HistoryValuePair,
  type HistoryValueView,
} from '@/composables/useEntityHistory';

const props = withDefaults(
  defineProps<{
    open: boolean;
    /** The employer the entity belongs to. */
    employerId: string | null | undefined;
    /** The audited item's own sort key (raw, e.g. `ORGUNIT#emp-1#unit-7`). */
    entityKey: string | null | undefined;
    /** Title override (translated); defaults to "Edit history". */
    title?: string;
    /** Subtitle, e.g. the entity's name (translated). */
    subtitle?: string;
    /** Attribute names whose values must never render (e.g. `Salary`). */
    maskedFields?: readonly string[];
    /** Optional attribute → display label map (translated). Wins over the defaults. */
    fieldLabels?: Readonly<Record<string, string>>;
  }>(),
  { maskedFields: () => [], fieldLabels: () => ({}) },
);

const emit = defineEmits<{ close: [] }>();

const { t, locale } = useI18n();

const history = useEntityHistory(
  () => props.employerId,
  () => props.entityKey,
  { enabled: () => props.open },
);

// The signed-in user resolves from their own claims (no request); a host
// without Pinia (isolated tests) simply has no `self`.
const self = useSelfActor();
const actorIds = computed(() => (props.open ? collectActorIds(history.entries.value) : []));
const { actors } = useHistoryActors(() => props.employerId, actorIds, self);

const rows = computed<HistoryRow[]>(() =>
  flattenHistory(history.entries.value, props.maskedFields),
);

const forbidden = computed(() => isHistoryForbidden(history.error.value));

const dateFmt = computed(
  () =>
    new Intl.DateTimeFormat(locale.value === 'km' ? 'km-KH' : 'en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }),
);

function formatWhen(at: string): string {
  const d = new Date(at);
  return Number.isFinite(d.getTime()) ? dateFmt.value.format(d) : '—';
}

function fieldLabel(field: string): string {
  if (!field) return '—';
  const own = props.fieldLabels[field];
  if (own) return own;
  if (isKnownHistoryField(field)) return t(`v2.ui.edit-history.fields.${field}`);
  return humanizeFieldName(field);
}

function actorView(row: HistoryRow) {
  return describeActor(row.actor, actors.value);
}

function valueView(row: HistoryRow, v: unknown): HistoryValueView {
  return describeHistoryValue(row.field, v, actors.value, props.maskedFields);
}

/** The "Key: value" lines of a `pairs` view (none for any other view). */
function pairsOf(v: HistoryValueView): HistoryValuePair[] {
  return v.kind === 'pairs' ? v.pairs : [];
}

/** One "Key: value" line; a masked key never shows its value. */
function pairText(p: HistoryValuePair): string {
  const value = p.masked ? t('v2.ui.edit-history.value-hidden') : viewText(p.value);
  return `${fieldLabel(p.key)}: ${value}`;
}

/** Plain text of a view, for the cases that render as one span. */
function viewText(v: HistoryValueView): string {
  switch (v.kind) {
    case 'empty':
      return '—';
    case 'ref':
      return t('v2.ui.edit-history.id-ref');
    case 'refs':
      return t('v2.ui.edit-history.id-refs', { count: v.count }, v.count);
    case 'date':
      return formatWhen(v.text);
    case 'system':
      return t('v2.ui.edit-history.system-actor');
    case 'pairs':
      return v.pairs.map(pairText).join('; ');
    default:
      return v.text;
  }
}

function viewTitle(v: HistoryValueView): string | undefined {
  if (v.kind === 'ref' || v.kind === 'system') return v.title;
  if (v.kind === 'actor') return v.title || undefined;
  return undefined;
}

function viewMuted(v: HistoryValueView): boolean {
  return v.kind === 'ref' || v.kind === 'refs' || v.kind === 'empty';
}

function actionLabel(action: HistoryRow['action']): string {
  return t(`v2.ui.edit-history.action.${action.toLowerCase()}`);
}

function maskLabel(row: HistoryRow): string {
  return row.mask === 'hidden'
    ? t('v2.ui.edit-history.value-hidden')
    : t('v2.ui.edit-history.value-not-kept');
}

const drawerTitle = computed(() => props.title ?? t('v2.ui.edit-history.title'));
const titleId = `edit-history-title-${useId()}`;

// ── Dialog behaviour: focus in, trap, Esc, focus back ──────────────────────
const panelRef = ref<HTMLElement | null>(null);
const closeRef = ref<HTMLButtonElement | null>(null);
let opener: HTMLElement | null = null;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(): HTMLElement[] {
  const panel = panelRef.value;
  if (!panel) return [];
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

function onKeydown(e: KeyboardEvent): void {
  if (!props.open) return;
  if (e.key === 'Escape') {
    // Runs in the capture phase on window, before Headless UI's (bubble)
    // listener of a modal underneath — so Esc closes THIS drawer only.
    e.preventDefault();
    e.stopImmediatePropagation();
    emit('close');
    return;
  }
  if (e.key !== 'Tab') return;
  const items = focusables();
  if (items.length === 0) {
    e.preventDefault();
    panelRef.value?.focus();
    return;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  const inside = !!active && !!panelRef.value?.contains(active);
  if (e.shiftKey && (!inside || active === first)) {
    e.preventDefault();
    e.stopImmediatePropagation();
    last.focus();
  } else if (!e.shiftKey && (!inside || active === last)) {
    e.preventDefault();
    e.stopImmediatePropagation();
    first.focus();
  }
}

function onFocusIn(e: FocusEvent): void {
  if (!props.open) return;
  const panel = panelRef.value;
  const target = e.target as Node | null;
  if (!panel || !target || panel.contains(target)) return;
  // Focus escaped the drawer (e.g. a click on the page behind) — bring it back.
  (closeRef.value ?? panel).focus();
}

function attach(): void {
  window.addEventListener('keydown', onKeydown, true);
  document.addEventListener('focusin', onFocusIn, true);
}

function detach(): void {
  window.removeEventListener('keydown', onKeydown, true);
  document.removeEventListener('focusin', onFocusIn, true);
}

watch(
  () => props.open,
  async (isOpen, wasOpen) => {
    if (isOpen && !wasOpen) {
      const active = document.activeElement;
      opener = active instanceof HTMLElement && active !== document.body ? active : null;
      attach();
      await nextTick();
      (closeRef.value ?? panelRef.value)?.focus();
    } else if (!isOpen && wasOpen) {
      detach();
      const back = opener;
      opener = null;
      await nextTick();
      if (back && back.isConnected) back.focus();
    }
  },
  { immediate: true, flush: 'post' },
);

onBeforeUnmount(() => {
  detach();
  if (props.open && opener?.isConnected) opener.focus();
  opener = null;
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="duration-150 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="duration-100 ease-in"
      leave-to-class="opacity-0"
    >
      <div v-if="open" class="relative z-[60]" data-testid="edit-history-dialog-root">
        <div
          class="fixed inset-0 bg-app-fg/40"
          aria-hidden="true"
          data-testid="edit-history-overlay"
          @click="emit('close')"
        />
        <section
          ref="panelRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
          class="fixed inset-y-0 right-0 flex h-full max-w-full flex-col border-l border-app-border bg-app-surface shadow-popup outline-none"
          style="width: 640px"
          data-testid="edit-history-dialog"
        >
          <div
            class="flex shrink-0 items-start justify-between gap-3 bg-gradient-to-r from-primary-600 to-primary px-6 py-4 text-white"
          >
            <div class="flex min-w-0 flex-col gap-0.5">
              <h2 :id="titleId" class="text-lg font-bold text-white">{{ drawerTitle }}</h2>
              <p v-if="subtitle" class="text-xs leading-[1.5] text-white/90">{{ subtitle }}</p>
            </div>
            <button
              ref="closeRef"
              type="button"
              class="inline-flex cursor-pointer items-center justify-center rounded-lg p-1 text-white/90 ring-1 ring-inset ring-white/40 transition-colors hover:bg-white/15 hover:text-white hover:ring-white/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              :aria-label="t('v2.ui.edit-history.close')"
              data-testid="edit-history-close"
              @click="emit('close')"
            >
              <X class="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <!-- Focusable so keyboard users can scroll it (axe scrollable-region-focusable). -->
          <div
            class="flex-1 overflow-y-auto px-5 py-4"
            tabindex="0"
            role="region"
            :aria-labelledby="titleId"
          >
            <div data-testid="edit-history">
              <EmptyState
                v-if="!history.available"
                :title="t('v2.ui.edit-history.unavailable-title')"
                :description="t('v2.ui.edit-history.unavailable-body')"
                data-testid="edit-history-unavailable"
              >
                <template #icon><History class="h-5 w-5" aria-hidden="true" /></template>
              </EmptyState>

              <div
                v-else-if="history.isPending.value && !history.isError.value"
                class="space-y-2"
                role="status"
                :aria-label="t('v2.ui.edit-history.loading')"
                data-testid="edit-history-loading"
              >
                <Skeleton v-for="i in 5" :key="i" class="h-9 w-full" />
              </div>

              <EmptyState
                v-else-if="history.isError.value && forbidden"
                :title="t('v2.ui.edit-history.forbidden-title')"
                :description="t('v2.ui.edit-history.forbidden-body')"
                data-testid="edit-history-forbidden"
              >
                <template #icon><History class="h-5 w-5" aria-hidden="true" /></template>
              </EmptyState>

              <EmptyState
                v-else-if="history.isError.value"
                :title="t('v2.ui.edit-history.error-title')"
                :description="t('v2.ui.edit-history.error-body')"
                data-testid="edit-history-error"
              >
                <template #icon><History class="h-5 w-5" aria-hidden="true" /></template>
                <template #action>
                  <Button
                    variant="secondary"
                    size="sm"
                    :loading="history.isFetching.value"
                    data-testid="edit-history-retry"
                    @click="history.refetch()"
                  >
                    {{ t('v2.ui.edit-history.retry') }}
                  </Button>
                </template>
              </EmptyState>

              <EmptyState
                v-else-if="rows.length === 0"
                :title="t('v2.ui.edit-history.empty-title')"
                :description="t('v2.ui.edit-history.empty-body')"
                data-testid="edit-history-empty"
              >
                <template #icon><History class="h-5 w-5" aria-hidden="true" /></template>
              </EmptyState>

              <template v-else>
                <p class="sr-only" role="status" aria-live="polite">
                  {{ t('v2.ui.edit-history.count', { count: rows.length }, rows.length) }}
                </p>
                <div class="overflow-x-auto rounded-xl border border-app-border" tabindex="0">
                  <!-- Fixed layout so long values wrap inside the panel instead of
                       widening the table past it. -->
                  <table
                    class="w-full table-fixed border-collapse text-md"
                    data-testid="edit-history-table"
                  >
                    <caption class="sr-only">
                      {{
                        drawerTitle
                      }}
                    </caption>
                    <colgroup>
                      <col class="w-[22%]" />
                      <col class="w-[22%]" />
                      <col class="w-[22%]" />
                      <col />
                    </colgroup>
                    <thead
                      class="bg-slate-50 text-left text-[11.5px] uppercase tracking-[0.04em] text-slate-500"
                    >
                      <tr>
                        <th scope="col" class="px-3 py-2 font-semibold">
                          {{ t('v2.ui.edit-history.col.when') }}
                        </th>
                        <th scope="col" class="px-3 py-2 font-semibold">
                          {{ t('v2.ui.edit-history.col.who') }}
                        </th>
                        <th scope="col" class="px-3 py-2 font-semibold">
                          {{ t('v2.ui.edit-history.col.field') }}
                        </th>
                        <th scope="col" class="px-3 py-2 font-semibold">
                          {{ t('v2.ui.edit-history.col.change') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in rows"
                        :key="row.id"
                        class="border-t border-app-border-soft align-top"
                        data-testid="edit-history-row"
                      >
                        <td class="min-w-[7rem] px-3 py-2 text-app-muted">
                          <time :datetime="row.at">{{ formatWhen(row.at) }}</time>
                        </td>
                        <td
                          class="min-w-[8rem] px-3 py-2 break-words"
                          data-testid="edit-history-who"
                        >
                          <template v-if="actorView(row).kind === 'actor'">
                            <span class="block text-app-fg" :title="viewTitle(actorView(row))">{{
                              viewText(actorView(row))
                            }}</span>
                          </template>
                          <span
                            v-else
                            :title="viewTitle(actorView(row)) ?? (row.actor || undefined)"
                            >{{ viewText(actorView(row)) }}</span
                          >
                        </td>
                        <td class="px-3 py-2">
                          <span class="font-medium text-app-fg">{{ fieldLabel(row.field) }}</span>
                          <span class="ml-1 text-[11px] text-app-muted"
                            >· {{ actionLabel(row.action) }}</span
                          >
                        </td>
                        <td class="px-3 py-2">
                          <span
                            v-if="row.mask"
                            class="italic text-app-muted"
                            :data-testid="`edit-history-mask-${row.mask}`"
                            >{{ maskLabel(row) }}</span
                          >
                          <span v-else class="break-words">
                            <span class="sr-only">{{ t('v2.ui.edit-history.old') }}: </span>
                            <span
                              :class="[
                                'line-through',
                                'text-app-muted',
                                viewMuted(valueView(row, row.before)) ? 'italic' : '',
                              ]"
                              :title="viewTitle(valueView(row, row.before))"
                              data-testid="edit-history-before"
                              ><template v-if="valueView(row, row.before).kind === 'pairs'"
                                ><span
                                  v-for="p in pairsOf(valueView(row, row.before))"
                                  :key="p.key"
                                  class="block"
                                  data-testid="edit-history-pair"
                                  >{{ pairText(p) }}</span
                                ></template
                              ><template v-else>{{
                                viewText(valueView(row, row.before))
                              }}</template></span
                            >
                            <span aria-hidden="true"> → </span>
                            <span class="sr-only">{{ t('v2.ui.edit-history.new') }}: </span>
                            <span
                              :class="
                                viewMuted(valueView(row, row.after))
                                  ? 'italic text-app-muted'
                                  : 'text-app-fg'
                              "
                              :title="viewTitle(valueView(row, row.after))"
                              data-testid="edit-history-after"
                              ><template v-if="valueView(row, row.after).kind === 'pairs'"
                                ><span
                                  v-for="p in pairsOf(valueView(row, row.after))"
                                  :key="p.key"
                                  class="block"
                                  data-testid="edit-history-pair"
                                  >{{ pairText(p) }}</span
                                ></template
                              ><template v-else>{{
                                viewText(valueView(row, row.after))
                              }}</template></span
                            >
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="history.hasNextPage.value" class="mt-3 flex justify-center">
                  <Button
                    variant="secondary"
                    size="sm"
                    :loading="history.isFetchingNextPage.value"
                    data-testid="edit-history-load-more"
                    @click="history.fetchNextPage()"
                  >
                    {{ t('v2.ui.edit-history.load-more') }}
                  </Button>
                </div>
              </template>
            </div>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
